// Copyright (c) 2025 Audere, All Rights Reserved

import { useTranslation } from "react-i18next";
import { Box, Button, List, ListItem, ListItemText, Paper, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InfoBanner from "./InfoBanner";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import { LABELING_STATUS, type TestUploadImageProgressProps } from "../types/projects";
import { getStatusIcon } from "./StatusIcon";
import { getRDTName } from "../utils/string";

const TestImageUploadProgress = ({
  testData,
  onProceed,
  onAddMoreImages,
}: TestUploadImageProgressProps) => {
  const { t } = useTranslation("TestImageUploadProgress");
  const errors: string[] = []; // Replace this with the actual error list from the API during integration
  const hasErrors = errors.length > 0;

  const isReadyForLabeling =
    testData?.image_labeling?.status === LABELING_STATUS.NOT_STARTED;

  const currentStatus = testData?.image_labeling?.status;

  const statusText =
    testData?.image_labeling?.status === LABELING_STATUS.COMPLETE
      ? t("done")
      : testData?.image_labeling?.status === LABELING_STATUS.IN_PROGRESS
        ? t("common:labelling")
        : t("assessingText");

  const AddMoreImagesButton = ({ uid }: { uid: string }) => (
    <Button
      variant="contained"
      onClick={() => onAddMoreImages(uid)}
      sx={styles.addMoreImagesButton}
    >
      {t("addMoreImages")}
    </Button>
  );

  return (
    <Box
      sx={{
        border: hasErrors ? `1px solid ${theme.palette.grey[400]}` : "none",
        p: hasErrors ? 2 : 0,
        mb: 2,
      }}
    >
      <Paper elevation={0} sx={styles.container}>
        <Box sx={{ mb: 1, wordBreak: "break-word" }}>
          <Typography variant="body1" sx={styles.text}>
            {getRDTName(testData)}
          </Typography>
        </Box>
        {isReadyForLabeling ? (
          <Box sx={styles.buttonGroup} display="flex" alignItems="center">
            <Button variant="outlined" onClick={() => onProceed(testData.rdt_uid)}>
              {t("proceedToLabeling")}
            </Button>
            <AddMoreImagesButton uid={testData.rdt_uid} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center">
            {getStatusIcon(currentStatus)}
            <Typography sx={{ ml: 1 }}>{statusText}</Typography>
            <AddMoreImagesButton uid={testData.rdt_uid} />
          </Box>
        )}
      </Paper>

      {hasErrors && (
        <Box sx={styles.errorBox}>
          <InfoBanner
            icon={<MailOutlineIcon fontSize="small" />}
            message={t("errorSummary")}
            hasError
          />
          <Box sx={styles.errorList}>
            <List component="ul" disablePadding sx={styles.errorListItems}>
              {errors.map((error, idx) => (
                <ListItem
                  component="li"
                  key={`error-${idx}`}
                  disableGutters
                  dense
                  sx={styles.errorListItem}
                >
                  <ListItemText
                    primary={
                      <Typography component="span" variant="body2">
                        {error}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TestImageUploadProgress;

const styles = createStyleSheet({
  container: {
    mt: 2,
    p: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: "4px",
    backgroundColor: theme.palette.grey[100],
  },
  text: {
    fontWeight: 500,
    fontSize: "18px",
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(1),
  },
  errorBox: {
    mt: 1,
  },
  errorList: {
    mt: 1,
    p: 1.5,
    borderRadius: "4px",
  },
  errorListItems: {
    ml: 2,
  },
  errorListItem: {
    display: "list-item",
    listStyleType: "disc",
    paddingLeft: 2,
    fontSize: "14px",
  },
  addMoreImagesButton: { ml: 2 },
});
